/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { type OnInit, Component, Input } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { DMValidators, FormControl, FormGroup } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { INFINITE_NUM_64, INVALID_NUM } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { ToolService } from 'app/common/services';
import { HyperCdpSchedule } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { merge } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ScheduleStrService } from '../../schedule-str.service';
import { FrequenciesE, MAX_VALUE } from './config-policy.const';
import { ConfigPolicyStrService, protectPeriodRangeE, protectPeriodUnitE, ScheduleNumE } from './config-policy-str.service';

@Component({
    selector: 'app-hyper-cdp-schedule-create-config-policy',
    templateUrl: './config-policy.component.html',
    styleUrls: ['./config-policy.component.scss'],
})
export class ConfigPolicyComponent implements OnInit {
    @Input() data: HyperCdpSchedule;
    @Input() type: ScheduleTypeE = ScheduleTypeE.Block;

    ScheduleTypeE = ScheduleTypeE;
    i18n = this.str;
    isSimpleMode = SWITCH.IS_SIMPLE_MODE;


    vm: FormGroup;
    errorMessage: any;

    minuteOrHourMax = 24;
    minSecond = 3;

    secondsOpts: SelectOption[] = [];
    everyOpts: SelectOption[] = [];
    retentionOfEveryOpts: SelectOption[] = [];
    hourList: SelectOption[] = [];
    dateOpt: SelectOption[] = [];

    atLeastOneCdpSchedule = false;
    placeholderSnapTag = this.str.rangeByteLengthErr(0, 32);

    retentionNumTip = '';
    fixedSecond = 30;

    protectPeriodOptions = this.scheduleStr.protectPeriodOptions;
    protectPeriodRangeE = protectPeriodRangeE;
    /** 各策略最小保护周期的最大值 */
    protectPeriodMax = {
        lun: protectPeriodRangeE.maxDay,
        per: protectPeriodRangeE.maxDay,
        day: protectPeriodRangeE.maxDay,
        week: protectPeriodRangeE.maxDay,
        month: protectPeriodRangeE.maxDay,
    };
    wormEnabled = false;
    showWorm = false;
    minuteMin = 1;

    MAX_VALUE = MAX_VALUE;

    /** 页面初始化原有计划数量 */
    initScheduleNum = 0;

    /** 固定周期最大保留个数 */
    fixedMaxNum = this.MAX_VALUE[ScheduleTypeE.Block].FIXED;
    /** 每天最大保留个数 */
    dailyMaxNum = this.MAX_VALUE[ScheduleTypeE.Block].DAILY;
    /** 每周最大保留个数 */
    weeklyMaxNum = this.MAX_VALUE[ScheduleTypeE.Block].WEEKLY;
    /** 每月最大保留个数 */
    monthlyMaxNum = this.MAX_VALUE[ScheduleTypeE.Block].MONTHLY;
    /** 固定周期最大保留个数范围提示 */
    fixedMaxNumTips = this.i18n.title.rangeResult(1, this.MAX_VALUE[ScheduleTypeE.Block].FIXED);

    constructor(
        private str: ConfigPolicyStrService,
        private tool: ToolService,
        private scheduleStr: ScheduleStrService,
    ) {
        this.hourList = this.str.hourList;
        this.dateOpt = this.str.dateOpt;
        this.everyOpts = this.str.everyOpts;
        this.secondsOpts = this.str.secondsOpts;
        this.retentionOfEveryOpts = this.str.retentionOfEveryOpts;
    }

    ngOnInit(): void {
        this.retentionNumTip = this.type === ScheduleTypeE.File ? this.i18n.title.retentionNumTip2 : this.i18n.title.retentionNumTip;
        this.fixedMaxNumTips = this.i18n.title.rangeResult(1, this.MAX_VALUE[this.type][FrequenciesE.FIXED]);
        this.fixedMaxNum = this.MAX_VALUE[this.type][FrequenciesE.FIXED];
        this.dailyMaxNum = this.MAX_VALUE[this.type][FrequenciesE.DAILY];
        this.weeklyMaxNum = this.MAX_VALUE[this.type][FrequenciesE.WEEKLY];
        this.monthlyMaxNum = this.MAX_VALUE[this.type][FrequenciesE.MONTHLY];

        this.minSecond = this.type === ScheduleTypeE.File ? 15 : 3;
        this.fixedSecond = this.type === ScheduleTypeE.File ? 30 : 10;
        this.showWorm = this.type === ScheduleTypeE.Block && (this.data && this.data.SECURESNAPENABLED === 'true' || !this.data) &&
            !this.isSimpleMode;
        this.initScheduleNum = this.data ? this.getScheduleNum(this.data) : ScheduleNumE.DefaultNum;
        this.buildVm();
        this.patchModifyValue();
        this.checkVmDisabled();
    }

    buildVm(): void {
        this.vm = new FormGroup({
            isFixed: new FormControl(false),
            fixed: new FormGroup({
                second: new FormControl(this.fixedSecond),
                minute: new FormControl(1),
                hour: new FormControl(1),
                timeUnit: new FormControl('second'),
                FREQUENCYSECURESNAPENABLED: new FormControl(false),
                FREQUENCYAUTODELETEENABLED: new FormControl(false),
                FREQUENCYPROTECTPERIOD: new FormControl(Number(protectPeriodRangeE.defaultDay)),
                FREQUENCYPERIODUNIT: new FormControl(protectPeriodUnitE.day),
                retentionDay: new FormControl(0),
                retentionHour: new FormControl(0),
                retentionMinute: new FormControl(0),
                retentionSecond: new FormControl(10),
                retentionNum: new FormControl(1),
                ...(this.type === ScheduleTypeE.File ?
                    {
                        FREQUENCYTAG: new FormControl('', {
                            validators: [
                                DMValidators.rangeByteLength(0, 32),
                                this.str.snapTagValidator({ FREQUENCYTAG: true }),
                            ],
                            updateOn: 'blur',
                        }),
                    } : {}),
            }),

            isDaily: new FormControl(false),
            daily: new FormGroup({
                DAYSECURESNAPENABLED: new FormControl(false),
                DAYAUTODELETEENABLED: new FormControl(false),
                DAYPROTECTIONPERIOD: new FormControl(Number(protectPeriodRangeE.defaultDay)),
                DAYPERIODUNIT: new FormControl(protectPeriodUnitE.day),
                hours: new FormControl([], {
                    validators: [this.validateArr],
                }),
                minute: new FormControl(0),
                retentionNum: new FormControl(1),
                ...(this.type === ScheduleTypeE.File ?
                    {
                        DAYTAG: new FormControl('', {
                            validators: [
                                DMValidators.rangeByteLength(0, 32),
                                this.str.snapTagValidator({ DAYTAG: true }),
                            ],
                            updateOn: 'blur',
                        }),
                    } : {}),
            }),

            isWeekly: new FormControl(false),
            weekly: new FormGroup({
                WEEKLYSECURESNAPENABLED: new FormControl(false),
                WEEKLYAUTODELETEENABLED: new FormControl(false),
                WEEKLYPROTECTIONPERIOD: new FormControl(Number(protectPeriodRangeE.defaultDay)),
                WEEKLYPERIODUNIT: new FormControl(protectPeriodUnitE.day),
                days: new FormControl([], {
                    validators: [this.validateArr],
                }),
                time: new FormControl('00:00'),
                retentionNum: new FormControl(1),
                ...(this.type === ScheduleTypeE.File ?
                    {
                        WEEKLYTAG: new FormControl('', {
                            validators: [
                                DMValidators.rangeByteLength(0, 32),
                                this.str.snapTagValidator({ WEEKLYTAG: true }),
                            ],
                            updateOn: 'blur',
                        }),
                    } : {}),
            }),

            isMonthly: new FormControl(false),
            monthly: new FormGroup({
                MONTHSECURESNAPENABLED: new FormControl(false),
                MONTHAUTODELETEENABLED: new FormControl(false),
                MONTHPROTECTIONPERIOD: new FormControl(1),
                MONTHPERIODUNIT: new FormControl(protectPeriodUnitE.day),
                days: new FormControl([], {
                    validators: [this.validateArr],
                }),
                time: new FormControl('00:00'),
                retentionNum: new FormControl(1),
                ...(this.type === ScheduleTypeE.File ?
                    {
                        MONTHTAG: new FormControl('', {
                            validators: [
                                DMValidators.rangeByteLength(0, 32),
                                this.str.snapTagValidator({ MONTHTAG: true }),
                            ],
                            updateOn: 'blur',
                        }),
                    } : {}),
            }),
            PROTECTPERIOD: new FormControl({
                value: '1',
                disabled: true,
            }, {
                validators: [
                    this.str.protectPeriodValidator,
                ],
                updateOn: 'blur',
            }),
            PROTECTPERIODUNIT: new FormControl({
                value: protectPeriodUnitE.day,
                disabled: true,
            }, {
                validators: [
                    this.str.protectPeriodUnitValidator,
                ],
                updateOn: 'blur',
            }),
            AUTODELETEENABLED: new FormControl({
                value: false,
                disabled: true,
            }),
        });
        this.errorMessage = {
            fixed: {
                retentionHour: {
                    required: this.str.err.retention,
                    notTimes: this.str.err.notTimes,
                },
                ...(this.type === ScheduleTypeE.File ?
                    {
                        FREQUENCYTAG: {
                            FREQUENCYTAG: this.i18n.err.userSnapSyncTagValid,
                            rangeByteLength: this.str.lengthErrMsg(0, 32),
                        },
                    } : {}),
            },
            daily: {
                hours: {
                    required: this.str.specifyTimeErr,
                },
                ...(this.type === ScheduleTypeE.File ?
                    {
                        DAYTAG: {
                            DAYTAG: this.i18n.err.userSnapSyncTagValid,
                            rangeByteLength: this.str.lengthErrMsg(0, 32),
                        },
                    } : {}),
            },
            weekly: {
                days: {
                    required: this.str.specifyTimeErr,
                },
                ...(this.type === ScheduleTypeE.File ?
                    {
                        WEEKLYTAG: {
                            WEEKLYTAG: this.i18n.err.userSnapSyncTagValid,
                            rangeByteLength: this.str.lengthErrMsg(0, 32),
                        },
                    } : {}),
            },
            monthly: {
                days: {
                    required: this.str.specifyTimeErr,
                },
                ...(this.type === ScheduleTypeE.File ?
                    {
                        MONTHTAG: {
                            MONTHTAG: this.i18n.err.userSnapSyncTagValid,
                            rangeByteLength: this.str.lengthErrMsg(0, 32),
                        },
                    } : {}),
            },
            PROTECTPERIOD: {
                required: this.str.create.nullValidation(this.str.create.protectTime),
                range: this.str.create.protectTimeValid(this.str.create.protectTime, protectPeriodRangeE.min, protectPeriodRangeE.maxYear),
            },
        };

        if (this.type === ScheduleTypeE.File && (!this.data || this.tool.isInvalidNum(this.data.FREQUENCYNUM))) {
            // 秒的默认值为30
            this.vm.get('fixed.second').setValue(30);
            // 单位默认值为小时
            this.vm.get('fixed.timeUnit').setValue('hour');
        }

        merge(
            this.vm.get('fixed.second').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.minute').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.hour').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.timeUnit').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.retentionDay').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.retentionHour').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.retentionMinute').valueChanges.pipe(distinctUntilChanged()),
            this.vm.get('fixed.retentionSecond').valueChanges.pipe(distinctUntilChanged()),
        ).pipe(debounceTime(10)).subscribe(() => {
            this.onRetentionTimeChange();
        });

        this.vm.get('fixed.retentionNum').valueChanges.pipe(
            distinctUntilChanged(),
            debounceTime(10),
        ).subscribe(() => {
            this.onRetentionCountChange();
        });
    }

    /**
     * 按时段改变
     */
    onRetentionTimeChange(): void {
        const fixedValue = this.vm.get('fixed').value;
        const seconds = this.getTimeValueObj(fixedValue).timeValue;
        const totalSeconds = fixedValue.retentionDay * 86400 +
            fixedValue.retentionHour * 3600 +
            fixedValue.retentionMinute * 60 +
            fixedValue.retentionSecond;

        const num = Math.max(1, Math.min(Math.ceil(totalSeconds / seconds), this.fixedMaxNum));
        this.vm.get('fixed.retentionNum').setValue(num);
        if (num !== totalSeconds / seconds) {
            this.onRetentionCountChange();
        }
    }

    /**
     * 按个数改变
     */
    onRetentionCountChange(): void {
        const fixedValue = this.vm.get('fixed').value;
        const seconds = this.getTimeValueObj(fixedValue).timeValue;
        const totalSeconds = seconds * fixedValue.retentionNum;

        const day = Math.floor(totalSeconds / 86400);
        const hour = Math.floor((totalSeconds - day * 86400) / 3600);
        const minute = Math.floor((totalSeconds - day * 86400 - hour * 3600) / 60);
        const second = totalSeconds - day * 86400 - hour * 3600 - minute * 60;
        this.vm.get('fixed.retentionDay').setValue(day);
        this.vm.get('fixed.retentionHour').setValue(hour);
        this.vm.get('fixed.retentionMinute').setValue(minute);
        this.vm.get('fixed.retentionSecond').setValue(second);
    }

    patchModifyValue(): void {
        if (!this.data) {
            return;
        }

        const data = this.data;
        const patchValue: any = {};

        this.patchFrequency(data, patchValue);
        this.patchDayMinute(data, patchValue);
        this.patchStartTimeOfWeek(data, patchValue);
        this.patchStartTimeOfMonth(data, patchValue);
        this.patchSecureSnapEnabled(data, patchValue);

        this.vm.patchValue(patchValue);
        this.vm.restore();
    }

    /**
     * 更新“固定周期”相关值
     * @param data “HyperCDP计划”数据
     * @param patchValue 要修改的表单值
     */
    patchFrequency(data: HyperCdpSchedule, patchValue): void {
        // 保留个数默认值1
        const retentionNumDefault = 1;

        if (!this.tool.isInvalidNum(data.FREQUENCYVALUE)) {
            const temp: any = {};
            const timeValue = Number(data.FREQUENCYVALUE);
            let timeUnit;
            let retentionNum: number;

            if (!this.tool.isInvalidNum(data.FREQUENCYNUM) && Number(data.FREQUENCYNUM) > 0) {
                retentionNum = Number(this.data.FREQUENCYNUM);
            } else {
                retentionNum = retentionNumDefault;
            }
            if (timeValue % 3600 === 0) {
                timeUnit = 'hour';
                temp.hour = timeValue / 3600;
            } else if (timeValue % 60 === 0) {
                timeUnit = 'minute';
                temp.minute = timeValue / 60;
            } else {
                timeUnit = 'second';
                temp.second = timeValue;
            }
            temp.timeUnit = timeUnit;

            const totalSeconds = timeValue * retentionNum;
            const day = Math.floor(Number(totalSeconds) / 86400);
            const hour = Math.floor((Number(totalSeconds) - Number(day) * 86400) / 3600);
            const minute = Math.floor((totalSeconds - day * 86400 - hour * 3600) / 60);
            const second = totalSeconds - day * 86400 - hour * 3600 - minute * 60;
            temp.retentionDay = day;
            temp.retentionHour = hour;
            temp.retentionMinute = minute;
            temp.retentionSecond = second;
            temp.retentionNum = retentionNum;
            temp.retentionType = 'count';
            if (this.type === ScheduleTypeE.File) {
                temp.FREQUENCYSECURESNAPENABLED = data.FREQUENCYSECURESNAPENABLED;
                temp.FREQUENCYTAG = this.data.FREQUENCYTAG;
            }

            if (this.type === ScheduleTypeE.File && data.FREQUENCYSECURESNAPENABLED) {
                temp.FREQUENCYAUTODELETEENABLED = data.FREQUENCYAUTODELETEENABLED;
                temp.FREQUENCYPROTECTPERIOD = Number(data.FREQUENCYPROTECTPERIOD);
                temp.FREQUENCYPERIODUNIT = data.FREQUENCYPERIODUNIT;
                this.setUnitRange(data.FREQUENCYPERIODUNIT, 'per', true);
            }

            patchValue.isFixed = true;
            patchValue.fixed = temp;
        }
    }

    /**
     * 更新“每天固定时间”相关值
     * @param data “HyperCDP计划”数据
     * @param patchValue 要修改的表单值
     */
    patchDayMinute(data: HyperCdpSchedule, patchValue): void {
        if (!this.tool.isInvalidNum(data.DAYMINUTE)) {
            const temp: any = {};

            temp.hours = JSON.parse((data as any).DAYHOURS);
            temp.minute = Number(data.DAYMINUTE);
            if (this.type === ScheduleTypeE.File && !data.DAYSECURESNAPENABLED ||
                data.SECURESNAPENABLED === 'false' && this.type === ScheduleTypeE.Block) {
                temp.retentionNum = Number(data.DAILYSNAPSHOTNUM);
            }

            if (this.type === ScheduleTypeE.File) {
                temp.DAYSECURESNAPENABLED = data.DAYSECURESNAPENABLED;
                temp.DAYTAG = this.data.DAYTAG;
            }

            if (this.type === ScheduleTypeE.File && data.DAYSECURESNAPENABLED) {
                temp.DAYAUTODELETEENABLED = data.DAYAUTODELETEENABLED;
                temp.DAYPROTECTIONPERIOD = Number(data.DAYPROTECTIONPERIOD);
                temp.DAYPERIODUNIT = data.DAYPERIODUNIT;
                this.setUnitRange(data.DAYPERIODUNIT, 'day', true);
            }

            patchValue.isDaily = true;
            patchValue.daily = temp;
        }
    }

    /**
     * 更新“每周固定时间”相关值
     * @param data “HyperCDP计划”数据
     * @param patchValue 要修改的表单值
     */
    patchStartTimeOfWeek(data: HyperCdpSchedule, patchValue): void {
        if (data.STARTTIMEOFWEEK) {
            const temp: any = {};

            temp.days = JSON.parse((data as any).WEEKLYDAYS);
            temp.time = data.STARTTIMEOFWEEK;
            if (this.type === ScheduleTypeE.File && !data.WEEKLYSECURESNAPENABLED ||
                data.SECURESNAPENABLED === 'false' && this.type === ScheduleTypeE.Block) {
                temp.retentionNum = Number(data.WEEKLYSNAPSHOTNUM);
            }

            if (this.type === ScheduleTypeE.File) {
                temp.WEEKLYSECURESNAPENABLED = data.WEEKLYSECURESNAPENABLED;
                temp.WEEKLYTAG = this.data.WEEKLYTAG;
            }

            if (this.type === ScheduleTypeE.File && data.WEEKLYSECURESNAPENABLED) {
                temp.WEEKLYAUTODELETEENABLED = data.WEEKLYAUTODELETEENABLED;
                temp.WEEKLYPROTECTIONPERIOD = Number(data.WEEKLYPROTECTIONPERIOD);
                temp.WEEKLYPERIODUNIT = data.WEEKLYPERIODUNIT;
                this.setUnitRange(data.WEEKLYPERIODUNIT, 'week', true);
            }

            patchValue.isWeekly = true;
            patchValue.weekly = temp;
        }
    }

    /**
     * 更新“每月固定时间”相关值
     * @param data “HyperCDP计划”数据
     * @param patchValue 要修改的表单值
     */
    patchStartTimeOfMonth(data: HyperCdpSchedule, patchValue): void {
        if (data.STARTTIMEOFMONTH) {
            const temp: any = {};

            temp.days = JSON.parse((data as any).MONTHDAYS);
            temp.time = data.STARTTIMEOFMONTH;
            if (this.type === ScheduleTypeE.File && !data.MONTHSECURESNAPENABLED ||
                data.SECURESNAPENABLED === 'false' && this.type === ScheduleTypeE.Block) {
                temp.retentionNum = Number(data.MONTHSNAPSHOTNUM);
            }

            if (this.type === ScheduleTypeE.File) {
                temp.MONTHSECURESNAPENABLED = data.MONTHSECURESNAPENABLED;
                temp.MONTHTAG = this.data.MONTHTAG;
            }

            if (this.type === ScheduleTypeE.File && data.MONTHSECURESNAPENABLED) {
                temp.MONTHAUTODELETEENABLED = data.MONTHAUTODELETEENABLED;
                temp.MONTHPROTECTIONPERIOD = Number(data.MONTHPROTECTIONPERIOD);
                temp.MONTHPERIODUNIT = data.MONTHPERIODUNIT;
                this.setUnitRange(data.MONTHPERIODUNIT, 'month', true);
            }

            patchValue.isMonthly = true;
            patchValue.monthly = temp;
        }
    }

    /**
     * 更新“安全快照”相关值
     * @param data “HyperCDP计划”数据
     * @param patchValue 要修改的表单值
     */
    patchSecureSnapEnabled(data: HyperCdpSchedule, patchValue): void {
        if (data.SECURESNAPENABLED === 'true' && this.type === ScheduleTypeE.Block) {
            this.wormEnabledChange(true);
            this.setUnitRange(data.PROTECTPERIODUNIT, 'lun');
            patchValue.PROTECTPERIOD = Number(data.PROTECTPERIOD);
            patchValue.PROTECTPERIODUNIT = data.PROTECTPERIODUNIT;
            patchValue.AUTODELETEENABLED = data.AUTODELETEENABLED === 'true';
        } else {
            this.wormEnabledChange(false);
        }
    }

    checkVmDisabled(): void {
        const vm = this.vm;
        const vmValue = vm.getRawValue();

        // 固定周期禁用状态
        if (!vmValue.isFixed) {
            vm.get('fixed').disable();
        } else {
            vm.get('fixed').enable();
        }

        // 每天执行禁用状态
        if (!vmValue.isDaily) {
            vm.get('daily').disable();
        } else {
            vm.get('daily').enable();
        }

        // 每周执行禁用状态
        if (!vmValue.isWeekly) {
            vm.get('weekly').disable();
        } else {
            vm.get('weekly').enable();
        }

        // 每月执行禁用状态
        if (!vmValue.isMonthly) {
            vm.get('monthly').disable();
        } else {
            vm.get('monthly').enable();
        }
    }

    validateDayAndHour(): void {
        if (!this.vm) {
            return;
        }

        if (this.vm.get('fixed.retentionType').value === 'count') {
            return;
        }

        const retentionDayCtrl = this.vm.get('fixed.retentionDay') as FormControl;
        const retentionHourCtrl = this.vm.get('fixed.retentionHour') as FormControl;
        const retentionSeconds = retentionDayCtrl.value * 86400 + retentionHourCtrl.value * 3600;
        const timeValueObj = this.getTimeValueObj(this.vm.get('fixed').value);

        let err = null;
        if (retentionSeconds === 0) {
            err = { required: true };
        } else if (retentionSeconds % timeValueObj.timeValue !== 0) {
            err = { notTimes: true };
        } else if (retentionSeconds / timeValueObj.timeValue > this.fixedMaxNum) {
            err = { exceed: true };
            const seconds = timeValueObj.timeValue * this.fixedMaxNum;
            const day = Math.floor(seconds / 86400);
            const hour = Math.floor((seconds - day * 86400) / 3600);
            this.errorMessage.fixed.retentionHour.exceed = this.str.exceedErr(day, hour);
        }

        retentionHourCtrl.setErrors(err);
        retentionHourCtrl.markAsTouched();
        retentionDayCtrl.setErrors(err);
        retentionDayCtrl.markAsTouched();
    }

    validateArr = (control: AbstractControl): ValidationErrors | null => {
        if (control.value.length === 0) {
            return { required: true };
        }

        return null;
    };

    getFixedPolicy(): { timeValue: number; retentionNum: any; FREQUENCYTAG: any } |
    { timeValue: number; retentionNum: any; FREQUENCYTAG?: undefined } {
        const fixedValue = (this.vm.get('fixed') as FormGroup).getRawValue();
        const timeValueObj = this.getTimeValueObj(fixedValue);
        const timeValue = timeValueObj.timeValue;
        const retentionNum = fixedValue.retentionNum;
        if (this.type === ScheduleTypeE.File && (this.vm.get('fixed') as FormGroup).changedValue) {
            const FREQUENCYTAG = (this.vm.get('fixed') as FormGroup).changedValue.FREQUENCYTAG;
            return {
                timeValue,
                retentionNum,
                FREQUENCYTAG,
            };
        } else {
            return {
                timeValue,
                retentionNum,
            };
        }
    }

    getTimeValueObj(fixedValue: any): { timeValue: number; timeUnitValue: number } {
        let timeValue: number;
        let timeUnitValue: number;

        if (fixedValue.timeUnit === 'second') {
            timeUnitValue = 1;
            timeValue = fixedValue.second;
        } else if (fixedValue.timeUnit === 'minute') {
            timeUnitValue = 60;
            timeValue = fixedValue.minute * 60;
        } else if (fixedValue.timeUnit === 'hour') {
            timeUnitValue = 3600;
            timeValue = fixedValue.hour * 3600;
        } else {}

        return { timeValue, timeUnitValue };
    }

    getPolicyConfig(): HyperCdpSchedule {
        const data: HyperCdpSchedule = {};
        const vmValue = this.vm.getRawValue();

        this.getFixedPolicyValue(data, vmValue);
        this.getDailyPolicyValue(data, vmValue);
        this.getWeeklyPolicyValue(data, vmValue);
        this.getMonthlyPolicyValue(data, vmValue);

        if (this.wormEnabled && this.type === ScheduleTypeE.Block) {
            data.SECURESNAPENABLED = true;
            data.AUTODELETEENABLED = vmValue.AUTODELETEENABLED;
            data.PROTECTPERIODUNIT = vmValue.PROTECTPERIODUNIT;
            data.PROTECTPERIOD = vmValue.PROTECTPERIOD;
        } else if (!this.wormEnabled && this.type === ScheduleTypeE.Block) {
            data.SECURESNAPENABLED = false;
        } else {}

        return data;
    }

    /**
     * 获取“固定周期”的值
     * @param data “HyperCDP计划”数据
     * @param vmValue 表单的值
     */
    getFixedPolicyValue(data: HyperCdpSchedule, vmValue): void {
        if (vmValue.isFixed) {
            const fixed = this.getFixedPolicy();
            data.FREQUENCYVALUE = String(fixed.timeValue);
            if (this.type === ScheduleTypeE.File) {
                data.FREQUENCYSECURESNAPENABLED = vmValue.fixed.FREQUENCYSECURESNAPENABLED;
            }
            if (this.type === ScheduleTypeE.File && vmValue.fixed.FREQUENCYSECURESNAPENABLED) {
                data.FREQUENCYPROTECTPERIOD = vmValue.fixed.FREQUENCYPROTECTPERIOD;
                data.FREQUENCYPERIODUNIT = vmValue.fixed.FREQUENCYPERIODUNIT;
                data.FREQUENCYAUTODELETEENABLED = vmValue.fixed.FREQUENCYAUTODELETEENABLED;
            } else {
                data.FREQUENCYNUM = String(fixed.retentionNum);
            }

            if (this.type === ScheduleTypeE.File) {
                data.FREQUENCYTAG = fixed.FREQUENCYTAG;
            }
        }
    }

    /**
     * 获取“每天执行”的值
     * @param data “HyperCDP计划”数据
     * @param vmValue 表单的值
     */
    getDailyPolicyValue(data: HyperCdpSchedule, vmValue): void {
        if (vmValue.isDaily) {
            data.DAYHOURS = vmValue.daily.hours;
            data.DAYMINUTE = vmValue.daily.minute;
            if (this.type === ScheduleTypeE.File) {
                data.DAYSECURESNAPENABLED = vmValue.daily.DAYSECURESNAPENABLED;
            }
            if (this.type === ScheduleTypeE.File && vmValue.daily.DAYSECURESNAPENABLED) {
                data.DAYPROTECTIONPERIOD = vmValue.daily.DAYPROTECTIONPERIOD;
                data.DAYPERIODUNIT = vmValue.daily.DAYPERIODUNIT;
                data.DAYAUTODELETEENABLED = vmValue.daily.DAYAUTODELETEENABLED;
            } else {
                data.DAILYSNAPSHOTNUM = vmValue.daily.retentionNum;
            }

            if (this.type === ScheduleTypeE.File && (this.vm.get('daily') as FormGroup).changedValue) {
                data.DAYTAG = (this.vm.get('daily') as FormGroup).changedValue.DAYTAG;
            }
        }
    }

    /**
     * 获取“每周执行”的值
     * @param data “HyperCDP计划”数据
     * @param vmValue 表单的值
     */
    getWeeklyPolicyValue(data: HyperCdpSchedule, vmValue): void {
        if (vmValue.isWeekly) {
            data.WEEKLYDAYS = vmValue.weekly.days;
            data.STARTTIMEOFWEEK = vmValue.weekly.time;
            if (this.type === ScheduleTypeE.File) {
                data.WEEKLYSECURESNAPENABLED = vmValue.weekly.WEEKLYSECURESNAPENABLED;
            }
            if (this.type === ScheduleTypeE.File && vmValue.weekly.WEEKLYSECURESNAPENABLED) {
                data.WEEKLYPROTECTIONPERIOD = vmValue.weekly.WEEKLYPROTECTIONPERIOD;
                data.WEEKLYPERIODUNIT = vmValue.weekly.WEEKLYPERIODUNIT;
                data.WEEKLYAUTODELETEENABLED = vmValue.weekly.WEEKLYAUTODELETEENABLED;
            } else {
                data.WEEKLYSNAPSHOTNUM = vmValue.weekly.retentionNum;
            }

            if (this.type === ScheduleTypeE.File && (this.vm.get('weekly') as FormGroup).changedValue) {
                data.WEEKLYTAG = (this.vm.get('weekly') as FormGroup).changedValue.WEEKLYTAG;
            }
        }
    }

    /**
     * 获取“每月执行”的值
     * @param data “HyperCDP计划”数据
     * @param vmValue 表单的值
     */
    getMonthlyPolicyValue(data: HyperCdpSchedule, vmValue): void {
        if (vmValue.isMonthly) {
            data.MONTHDAYS = vmValue.monthly.days;
            data.STARTTIMEOFMONTH = vmValue.monthly.time;
            if (this.type === ScheduleTypeE.File) {
                data.MONTHSECURESNAPENABLED = vmValue.monthly.MONTHSECURESNAPENABLED;
            }
            if (this.type === ScheduleTypeE.File && vmValue.monthly.MONTHSECURESNAPENABLED) {
                data.MONTHPROTECTIONPERIOD = vmValue.monthly.MONTHPROTECTIONPERIOD;
                data.MONTHPERIODUNIT = vmValue.monthly.MONTHPERIODUNIT;
                data.MONTHAUTODELETEENABLED = vmValue.monthly.MONTHAUTODELETEENABLED;
            } else {
                data.MONTHSNAPSHOTNUM = vmValue.monthly.retentionNum;
            }

            if (this.type === ScheduleTypeE.File && (this.vm.get('monthly') as FormGroup).changedValue) {
                data.MONTHTAG = (this.vm.get('monthly') as FormGroup).changedValue.MONTHTAG;
            }
        }
    }

    getChangedPolifyConfig(): HyperCdpSchedule {
        const data: HyperCdpSchedule = this.getPolicyConfig();
        const changedRawValue = this.vm.changedRawValue;

        if (!changedRawValue) {
            return {};
        }

        this.setFixedConfigValue(data, changedRawValue);
        this.setDailyConfigValue(data, changedRawValue);
        this.setWeeklyConfigValue(data, changedRawValue);
        this.setMonthlyConfigValue(data, changedRawValue);

        if (data.SECURESNAPENABLED) {
            delete data.FREQUENCYNUM;
            delete data.DAILYSNAPSHOTNUM;
            delete data.WEEKLYSNAPSHOTNUM;
            delete data.MONTHSNAPSHOTNUM;
        }

        return data;
    }

    /**
     * 设置“固定周期”的值
     * @param data “HyperCDP计划”数据
     * @param changedRawValue 表单改变的值
     */
    setFixedConfigValue(data: HyperCdpSchedule, changedRawValue): void {
        if (Object.prototype.hasOwnProperty.call(changedRawValue, 'isFixed') && !changedRawValue.isFixed) {
            data.FREQUENCYVALUE = String(INVALID_NUM);
            data.FREQUENCYNUM = String(INVALID_NUM);
            if (this.type === ScheduleTypeE.File) {
                data.FREQUENCYSECURESNAPENABLED = false;
                data.FREQUENCYAUTODELETEENABLED = false;
                data.FREQUENCYPROTECTPERIOD = INFINITE_NUM_64;
                data.FREQUENCYPERIODUNIT = String(INVALID_NUM);
                data.FREQUENCYTAG = '';
            }
        }
    }

    /**
     * 设置“每天执行”的值
     * @param data “HyperCDP计划”数据
     * @param changedRawValue 表单改变的值
     */
    setDailyConfigValue(data: HyperCdpSchedule, changedRawValue): void {
        if (Object.prototype.hasOwnProperty.call(changedRawValue, 'isDaily') && !changedRawValue.isDaily) {
            data.DAYHOURS = [];
            data.DAYMINUTE = String(INVALID_NUM);
            data.DAILYSNAPSHOTNUM = String(INVALID_NUM);
            if (this.type === ScheduleTypeE.File) {
                data.DAYSECURESNAPENABLED = false;
                data.DAYAUTODELETEENABLED = false;
                data.DAYPROTECTIONPERIOD = INFINITE_NUM_64;
                data.DAYPERIODUNIT = String(INVALID_NUM);
                data.DAYTAG = '';
            }
        }
    }

    /**
     * 设置“每周执行”的值
     * @param data “HyperCDP计划”数据
     * @param changedRawValue 表单改变的值
     */
    setWeeklyConfigValue(data: HyperCdpSchedule, changedRawValue): void {
        if (Object.prototype.hasOwnProperty.call(changedRawValue, 'isWeekly') && !changedRawValue.isWeekly) {
            data.WEEKLYDAYS = [];
            data.STARTTIMEOFWEEK = '';
            data.WEEKLYSNAPSHOTNUM = String(INVALID_NUM);
            if (this.type === ScheduleTypeE.File) {
                data.WEEKLYSECURESNAPENABLED = false;
                data.WEEKLYAUTODELETEENABLED = false;
                data.WEEKLYPROTECTIONPERIOD = INFINITE_NUM_64;
                data.WEEKLYPERIODUNIT = String(INVALID_NUM);
                data.WEEKLYTAG = '';
            }
        }
    }

    /**
     * 设置“每月执行”的值
     * @param data “HyperCDP计划”数据
     * @param changedRawValue 表单改变的值
     */
    setMonthlyConfigValue(data: HyperCdpSchedule, changedRawValue): void {
        if (Object.prototype.hasOwnProperty.call(changedRawValue, 'isMonthly') && !changedRawValue.isMonthly) {
            data.MONTHDAYS = [];
            data.STARTTIMEOFMONTH = '';
            data.MONTHSNAPSHOTNUM = String(INVALID_NUM);
            if (this.type === ScheduleTypeE.File) {
                data.MONTHSECURESNAPENABLED = false;
                data.MONTHAUTODELETEENABLED = false;
                data.MONTHPROTECTIONPERIOD = INFINITE_NUM_64;
                data.MONTHPERIODUNIT = String(INVALID_NUM);
                data.MONTHTAG = '';
            }
        }
    }

    checkAtLeastOneStatus(): void {
        const isLeastOneStatus = !(this.vm.value.isWeekly || this.vm.value.isMonthly || this.vm.value.isDaily || this.vm.value.isFixed);
        if (isLeastOneStatus) {
            this.atLeastOneCdpSchedule = true;
        } else {
            this.atLeastOneCdpSchedule = false;
        }
    }

    onStrategyChange(): void {
        this.checkAtLeastOneStatus();
        this.checkVmDisabled();
    }

    /**
     * worm开关
     */
    wormEnabledChange(value: boolean): void {
        this.wormEnabled = value;

        const protectPeriod = this.vm.get('PROTECTPERIOD');
        const protectPeriodUnit = this.vm.get('PROTECTPERIODUNIT');
        const autoDelete = this.vm.get('AUTODELETEENABLED');

        if (value) {
            protectPeriod.enable();
            protectPeriodUnit.enable();
            autoDelete.enable();
            protectPeriod.markAsTouched();
            protectPeriodUnit.markAsTouched();
            autoDelete.markAsTouched();
        } else {
            protectPeriod.disable();
            protectPeriodUnit.disable();
            autoDelete.disable();
        }
    }

    unitChanged({ value }: { value: string }, type: string): void {
        this.setUnitRange(value, type);
    }

    setUnitRange(value: string, type: string, modifyMode = false): void {
        switch (value) {
            case protectPeriodUnitE.day:
                if (!modifyMode) {
                    this.setPolicyDefaultValue(type, Number(protectPeriodRangeE.defaultDay));
                }
                this.protectPeriodMax[type] = protectPeriodRangeE.maxDay;
                break;
            case protectPeriodUnitE.month:
                if (!modifyMode) {
                    this.setPolicyDefaultValue(type, Number(protectPeriodRangeE.defaultMonth));
                }
                this.protectPeriodMax[type] = protectPeriodRangeE.maxMonth;
                break;
            case protectPeriodUnitE.year:
                if (!modifyMode) {
                    this.setPolicyDefaultValue(type, Number(protectPeriodRangeE.defaultYear));
                }
                this.protectPeriodMax[type] = protectPeriodRangeE.maxYear;
                break;
            default:
                break;
        }
    }

    setPolicyDefaultValue(type: string, currentValue: number): void {
        switch (type) {
            case 'lun':
                this.vm.get('PROTECTPERIOD').setValue(currentValue);
                break;
            case 'per':
                this.vm.get('fixed.FREQUENCYPROTECTPERIOD').setValue(currentValue);
                break;
            case 'day':
                this.vm.get('daily.DAYPROTECTIONPERIOD').setValue(currentValue);
                break;
            case 'week':
                this.vm.get('weekly.WEEKLYPROTECTIONPERIOD').setValue(currentValue);
                break;
            case 'month':
                this.vm.get('monthly.MONTHPROTECTIONPERIOD').setValue(currentValue);
                break;
            default:
                break;
        }
    }

    securityEnabledChange(value: boolean, type: string): void {
        const frequencySecurityArray = ['fixed.FREQUENCYPROTECTPERIOD', 'fixed.FREQUENCYPERIODUNIT', 'fixed.FREQUENCYAUTODELETEENABLED'];
        const daySecurityArray = ['daily.DAYPROTECTIONPERIOD', 'daily.DAYPERIODUNIT', 'daily.DAYAUTODELETEENABLED'];
        const weekSecurityArray = ['weekly.WEEKLYPROTECTIONPERIOD', 'weekly.WEEKLYPERIODUNIT', 'weekly.WEEKLYAUTODELETEENABLED'];
        const monthSecurityArray = ['monthly.MONTHPROTECTIONPERIOD', 'monthly.MONTHPERIODUNIT', 'monthly.MONTHAUTODELETEENABLED'];
        switch (type) {
            case 'per':
                if (value) {
                    frequencySecurityArray.forEach((item) => {
                        this.vm.get(item).enable();
                    });
                } else {
                    frequencySecurityArray.forEach((item) => {
                        this.vm.get(item).disable();
                    });
                }
                break;
            case 'day':
                if (value) {
                    this.vm.get('daily.retentionNum').disable();
                    daySecurityArray.forEach((item) => {
                        this.vm.get(item).enable();
                    });
                } else {
                    this.vm.get('daily.retentionNum').enable();
                    daySecurityArray.forEach((item) => {
                        this.vm.get(item).disable();
                    });
                }
                break;
            case 'week':
                if (value) {
                    this.vm.get('weekly.retentionNum').disable();
                    weekSecurityArray.forEach((item) => {
                        this.vm.get(item).enable();
                    });
                } else {
                    this.vm.get('weekly.retentionNum').enable();
                    weekSecurityArray.forEach((item) => {
                        this.vm.get(item).disable();
                    });
                }
                break;
            case 'month':
                if (value) {
                    this.vm.get('monthly.retentionNum').disable();
                    monthSecurityArray.forEach((item) => {
                        this.vm.get(item).enable();
                    });
                } else {
                    this.vm.get('monthly.retentionNum').enable();
                    monthSecurityArray.forEach((item) => {
                        this.vm.get(item).disable();
                    });
                }
                break;
            default:
                break;
        }
    }

    /**
     * 获取当前页HyperCDP计划保留个数
     */
    getScheduleNum(policyConfig: HyperCdpSchedule): number {
        if (policyConfig.SECURESNAPENABLED === 'true' || policyConfig.SECURESNAPENABLED === true) {
            return 0;
        }

        let { FREQUENCYNUM = ScheduleNumE.DefaultNum,
            DAILYSNAPSHOTNUM = ScheduleNumE.DefaultNum,
            WEEKLYSNAPSHOTNUM = ScheduleNumE.DefaultNum,
            MONTHSNAPSHOTNUM = ScheduleNumE.DefaultNum } =
            policyConfig;

        // 修改界面的初始值，保留个数可能为-1
        FREQUENCYNUM = Number(FREQUENCYNUM) > 0 ? Number(FREQUENCYNUM) : 0;
        DAILYSNAPSHOTNUM = Number(DAILYSNAPSHOTNUM) > 0 ? Number(DAILYSNAPSHOTNUM) : 0;
        WEEKLYSNAPSHOTNUM = Number(WEEKLYSNAPSHOTNUM) > 0 ? Number(WEEKLYSNAPSHOTNUM) : 0;
        MONTHSNAPSHOTNUM = Number(MONTHSNAPSHOTNUM) > 0 ? Number(MONTHSNAPSHOTNUM) : 0;

        return FREQUENCYNUM + DAILYSNAPSHOTNUM + WEEKLYSNAPSHOTNUM + MONTHSNAPSHOTNUM;
    }

    exceedTheLimitNum(reservedNum: number): boolean {
        const curPolicyConfig = this.getPolicyConfig();
        const curScheduleNum = this.getScheduleNum(curPolicyConfig);
        return (reservedNum + curScheduleNum - this.initScheduleNum) > ScheduleNumE.MaxNum;
    }

    validate(): boolean {
        this.checkAtLeastOneStatus();
        if (this.atLeastOneCdpSchedule) {
            return false;
        }

        this.vm.validate();
        return this.vm.valid;
    }
}
